import cookie from '@ms/utils/cookie.js'
import { prefixLink } from '@ms/config/app'
import { appHeaders } from 'crypto-fetch/headers'
import jsBridge from '@ms/bridge/jsBridge'
import { isAndroid, isApp, isIOS, isIosApp } from './device'
import { compireVersion } from './appVersion'
import {
  APP_HOME,
  APP_SETTINGS,
  APP_UPGRADE,
  APP_LOGIN,
  APP_SIGNUP,
  APP_FORGOT_ACCOUNT,
  APP_ACCOUNT,
  APP_ME,
  APP_VOUCHER,
  APP_LIVE_LIST,
  APP_SPORT_LIST,
  APP_MATCH_DETAIL,
  APP_CODE_LIST,
  APP_ORDER_DETAILS,
  APP_JACKPOT_ORDER_DETAILS,
  APP_VIRTUAL_BET_HISTORY,
  APP_DEPOSIT,
  APP_WITHDRAW,
  APP_TRANSACTION,
  APP_TRANSACTION_DETAILS,
  APP_GAMES,
  APP_SPIN
} from '@ms/bridge/shortlink'

const addQuery = (url, query) => {
  const queryValue = query.toString()
  return url + (queryValue ? `?${queryValue}` : '')
}

/**
 * 与 app 通信（app 内）
 * @param {String} type        通信类型
 * @param {Object} option      参数
 * @param {Function} callback  回调函数，第一个参数为 app 传递的值
 */
function noticeApp(type, option = null, callback = null) {
  if (!type) return
  jsBridge.callHandler(type, option, callback)
}

/**
 * 跳转至app某页面
 * @param  {string} url
 * @param  {string} webpageUrl 网页链接, 非 app 跳转此页面
 */
export const tryJumpAppPage = (url = '', webpageUrl = '') => {
  if (isApp()) {
    const actionType = 'action_jump_page'
    if (/^msport:/.test(url) || isIOS()) {
      noticeApp(actionType, { param: url })
    } else {
      noticeApp(actionType, { param: `msport://web?url=${url}` })
    }
  } else {
    if (webpageUrl) {
      location.href = webpageUrl
    }
  }
}

// 唤起 app（app外）
export const openApp = (path = '') => {
  if (isAndroid() || isIOS()) {
    path = path ? path.split('jumpUrl=') : ''
    const _path = path.length ? `${path[0]}jumpUrl=${encodeURIComponent(path[1])}` : ''
    location.href = `msport://hopegaming.com${_path}`
  }
}

// 尝试从浏览器打开 app
export const tryOpenApp = (link = '', { onSuccess = null, onfailed = null, force = false } = {}) => {
  if (isApp()) return

  if (/[?&]appOpen/.test(location.search) || force) {
    if (!link) {
      link = link || `/params?jumpUrl=${location.href}`
    }
    link = link ? link.split('jumpUrl=') : ''
    const _path = link.length ? `${link[0]}jumpUrl=${encodeURIComponent(link[1])}` : ''
    try {
      location.href = `msport://hopegaming.com${_path}`

      // callback when failed
      if (onfailed) {
        const loc = location.href
        setTimeout(() => {
          if (location.href === loc && !document.hidden) {
            onfailed && onfailed()
          } else {
            onSuccess && onSuccess()
          }
        }, 2000)
      }
    } catch (error) {
      onfailed && onfailed()
      console.error(error)
    }
  }
}

export const gotoAppHome = (query = '') => tryJumpAppPage(APP_HOME + (query || ''))
export const gotoAppSettings = () => tryJumpAppPage(APP_SETTINGS)
export const gotoAppUpgrade = () => tryJumpAppPage(APP_UPGRADE)

export const gotoAppLogin = () => tryJumpAppPage(APP_LOGIN)
export const gotoAppSignup = () => tryJumpAppPage(APP_SIGNUP)
export const gotoAppForgotAccount = () => tryJumpAppPage(APP_FORGOT_ACCOUNT)
export const gotoAppAccount = () => tryJumpAppPage(APP_ACCOUNT)
export const gotoAppMe = () => tryJumpAppPage(APP_ME)
export const gotoAppVoucher = () => tryJumpAppPage(APP_VOUCHER)

export const gotoAppLiveList = ({ sportId = '' } = {}) => {
  const query = new URLSearchParams()
  if (sportId) query.append('SPORT_ID', sportId)
  const applink = addQuery(APP_LIVE_LIST, query)
  return tryJumpAppPage(applink)
}

// 跳转比赛列表页，默认 soccer
export const gotoAppSportList = ({ sport = 'Soccer', sportId = 'sr:sport:1', customLeagues = '', filterTime = '', orderId = '' } = {}) => {
  const query = new URLSearchParams({
    SPORT_ID: sportId,
    SPORT: sport
  })

  if (customLeagues) query.append('CUSTOM_LEAGUES', customLeagues)
  if (filterTime) query.append('FILTER_TIME', filterTime)
  if (orderId) query.append('orderId', orderId)

  const applink = addQuery(APP_SPORT_LIST, query)
  return tryJumpAppPage(applink)
}

export const gotoAppMatchDetail = ({ eventId = '', sportId = '', tab = '' } = {}) => {
  const query = new URLSearchParams({ eventId })
  if (sportId) query.append('sportId', sportId)
  if (tab) query.append('tab', tab)
  const applink = addQuery(APP_MATCH_DETAIL, query)
  return tryJumpAppPage(applink)
}

export const gotoAppCodeList = () => tryJumpAppPage(APP_CODE_LIST)
export const gotoAppVirtualBetHistory = () => tryJumpAppPage(APP_VIRTUAL_BET_HISTORY)

export const gotoOrderDetail = (orderId = '') => tryJumpAppPage(`${APP_ORDER_DETAILS}?orderId=${orderId}`)
export const gotoJackpotOrderDetail = (orderId = '') => tryJumpAppPage(`${APP_JACKPOT_ORDER_DETAILS}?orderId=${orderId}`)

export const gotoAppDeposit = (query = '') => tryJumpAppPage(APP_DEPOSIT + (query || ''))
export const gotoAppWithdraw = (query = '') => tryJumpAppPage(APP_WITHDRAW + (query || ''))

/**
 * goto app transaction
 * @param {String} ticketId
 * @param {Boolean} isHistory
 * @param {String} tradeId optional 可选，app 参数不一致导致有些地方传的是 tradeId
 * @returns
 */
export const gotoAppTransaction = ({ ticketId = '', isHistory = false, tradeId = '', waplink = '' } = {}) => {
  const query = new URLSearchParams()
  // 优先使用 ticketId (app 参数不一致)
  if (ticketId) {
    query.append('ticketId', ticketId)
  } else if (tradeId) {
    query.append('tradeId', tradeId)
  }
  if (isHistory) query.append('isHistory', 1)

  const applink = addQuery(APP_TRANSACTION, query)
  return tryJumpAppPage(applink, waplink)
}

export const gotoAppTransactionDetails = (tradeId = '') => tryJumpAppPage(`${APP_TRANSACTION_DETAILS}?tradeId=${tradeId}`)

export const gotoAppGames = () => tryJumpAppPage(APP_GAMES)
export const gotoAppSpin = () => tryJumpAppPage(APP_SPIN)
export const goGames = () => {
  isApp() ? gotoAppGames() : (location.href = prefixLink('/games'))
}

// 返回上一页
export const tryGoBack = (defaultBack = true) => {
  if (isApp()) {
    noticeApp('action_go_back')
  } else {
    defaultBack && window.goBack && window.goBack()
  }
}

export const reloadAppWebview = () => noticeApp('reload')

// webview 关闭前回调
export const tryBeforeCloseAppWebview = (func, { stopClose = false } = {}) => {
  if (!isApp()) return

  jsBridge.registerHandler('webviewBeforeClose', (type, responseCallback) => {
    console.log('webviewBeforeClose type:', type)
    func && func(type)
    const resText = stopClose ? '' : 'close'
    responseCallback && responseCallback(resText)
  })
}

export const tryCloseAppWebview = (fallback) => {
  if (isApp()) {
    noticeApp('action_close_web_view')
  } else {
    fallback && fallback()
  }
}

// 关闭 webview 并传递参数 autoLaunch, 1 表示通知 app 继续操作
export const tryCloseAppWebviewWithAutoLaunch = () => {
  if (!isApp()) return
  const autoLaunch = /autoLaunch=1/i.test(location.search)
  noticeApp('action_close_web_view', {
    autoLaunch: autoLaunch ? 1 : 2
  })
}

export const tryAppRefreshBalance = (balance) => {
  if (isApp()) {
    const actionType = 'action_refresh_balance'
    if (balance !== null && balance !== undefined) {
      noticeApp(actionType, { balance })
    } else {
      noticeApp(actionType)
    }
  }
}

/**
 * app下载图片
 * @param  {String} image  下载图片-base64格式
 * @param  {Function} fallback  非 app 使用 wap 下载
 */
export function tryAppSaveImage(image, fallback) {
  if (isApp()) {
    noticeApp('action_save_image', { image })
  } else {
    fallback && fallback()
  }
}

/**
 * 通知 app 点击 booking codes
 * @param  {Number} url
 * @return {}
 */
export function tryAppOpenBookingCodes(shareCode = '', fallback) {
  if (isApp()) {
    noticeApp('booking_codes_click', { shareCode })
  } else {
    fallback && fallback()
  }
}

export const updateAppWebviewHeight = () => noticeApp('action_update_height')

// 打开/关闭 app virtual video 视频区域
export const tryAppToggleVirtualVideo = (visible = false) => {
  if (isApp()) {
    noticeApp('action_virtual_video_open', { openVideo: visible })
  }
}

/**
 * 同步 kyc 信息给 app
 * @param {Object} data  kyc json 数据
 */
export const syncKycInfoToApp = (data = {}) => noticeApp('action_sync_kyc_info', data)

/**
 * 同步登录态给 app
 */
export const trySyncLoginStatusToApp = () => {
  if (!isApp()) return
  const option = {
    refreshToken: encodeURIComponent(cookie.get('refreshToken')),
    accessToken: encodeURIComponent(cookie.get('accessToken')),
    userId: encodeURIComponent(cookie.get('userId'))
  }

  if (isIosApp()) {
    // 适配世界杯专题页 登陆后不需要ios主动刷新页面
    const isWorldCup = location.pathname.match(/(\S*)\/msport_world_cup/)
    option.isRefresh = isWorldCup ? 0 : 1
  }

  noticeApp('action_login', option)
}

/**
 * 在app内调用wap分享唤起应用
 * @param  {String} title       分享标题
 * @param  {String} description 分享描述
 * @param  {String} url         分享链接
 * @param  {String} content     分享文案
 * @param  {String} image       分享图片链接（base64格式，如果为该类型不需要传imageUrl参数）
 * @param  {String} imageUrl    分享图片链接（png、jpg...等格式，如果为该类型不需要传bitmapData参数）
 * @param  {String} channel     分享渠道 whatsapp,facebook,twitter,save,copy_link,more
 * @param  {String} from        分享平台,可不传
 * @return {}
 */
export function shareToAppPlatform({ title, description, url, content, image, imageUrl, channel, from }) {
  if (!isApp()) return

  noticeApp('action_share_to_platform', {
    title,
    description,
    url,
    content,
    image,
    channel,
    imageUrl,
    from
  })
}

// 手动清空 jsBridge 消息队列 (app 保存图片只要绘图会触发自动保存)
export function tryClearBridgeQueue() {
  if (!document) return
  const iframe = document.querySelector('iframe[src^="yy://return/_fetchQueue"]')
  if (iframe) {
    iframe.src = 'yy://return/_fetchQueue/'
  }
}

// 通知 app 刷新指定数据
export const tryAppRefreshStats = (key = '') => {
  if (isApp()) {
    noticeApp('action_web_trigger_refresh', key)
  }
}

export const tryAppAddCalendarReminder = (data) => {
  if (isApp()) {
    noticeApp('action_calendar_reminder', data)
  }
}

// use app request method
export const requestFromApp = ({ url, query, method = 'get' }) => {
  return new Promise((resolve) => {
    noticeApp(
      'native_request',
      {
        url,
        query,
        method
      },
      resolve
    )
  })
}

// 客服系统：1开始对话2结束对话3新消息4最小化，startChat,endChat,newMessage,minimize
export const tryTellAppChatStats = (type, newMessage = 0) => {
  isApp() && noticeApp('action_customer_service_chat', { type, number: newMessage })
}

/**
 * 通知 app 下单成功
 */
export function tryTellAppBetSuccess() {
  if (isApp() && compireVersion({ ios: '1.0.5' })) {
    noticeApp('action_notice_webBet')
  }
}

/**
 * 通知 app 注册成功
 */
export function tryTellAppRegSuccess() {
  if (isApp() && compireVersion({ ios: '1.0.5' })) {
    noticeApp('action_notice_webRegister')
  }
}

/**
 * 通知 app 跳转并加载booking code
 */
export function tryAppLoadBookingCode(code, jumpUrl) {
  if (!isApp()) return

  const data = { jumpUrl }

  if (isIosApp()) {
    data.book_code = code
  } else {
    data.booking_code = code
  }

  return noticeApp('action_booking_code', data)
}

/**
 * 获取app给的信息放在header里
 */
export function trySetHeadersFromApp(params = '') {
  if (!isApp()) return
  noticeApp('action_param', params, (res) => {
    try {
      Object.assign(appHeaders.value, JSON.parse(res))
      appHeaders.status = true
    } catch (error) {
      console.error(error)
    }
  })
}

// 前往客服聊天
export const gotoChatWithApp = () => {
  const chatLink = `${location.origin}${prefixLink('/support/about/contact-us?chat=1')}`
  if (
    isApp() &&
    compireVersion({
      ios: '1.2.6',
      iosGame: '1.1.4',
      android: '1.8.2'
    })
  ) {
    return tryJumpAppPage(chatLink)
  } else {
    location.href = chatLink
  }
}
